import { Crosshair } from 'lucide-react';
import { useTriggers } from '../../../lib/useTriggers';
import type { Account, UseCaseItem, AccountTrigger } from '../../../types';

interface Props {
  account: Account;
  useCases: UseCaseItem[];
}

function matchTriggersToUseCase(useCase: UseCaseItem, triggers: AccountTrigger[]): AccountTrigger[] {
  const ucTitleLower = useCase.title.toLowerCase();
  return triggers.filter((t) => {
    if (Array.isArray(t.recommended_use_cases)) {
      for (const rec of t.recommended_use_cases) {
        if (typeof rec === 'string' && rec.toLowerCase().includes(ucTitleLower)) return true;
        if (typeof rec === 'string' && ucTitleLower.includes(rec.toLowerCase())) return true;
      }
    }
    return false;
  });
}

function UrgencyDot({ score }: { score: number }) {
  const color = score >= 8 ? 'bg-app-critical' : score >= 5 ? 'bg-app-warning' : 'bg-app-muted';
  return <div className={`w-2 h-2 rounded-full ${color} flex-shrink-0`} title={`Urgency: ${score}/10`} />;
}

export default function WhyNowMap({ account, useCases }: Props) {
  const { triggers } = useTriggers(account.id, account.org_id);

  if (triggers.length === 0 || useCases.length === 0) return null;

  const mappings = useCases.map((uc) => ({
    useCase: uc,
    matched: matchTriggersToUseCase(uc, triggers),
  }));

  const hasAnyMatch = mappings.some((m) => m.matched.length > 0);
  if (!hasAnyMatch) return null;

  return (
    <div className="bg-app-surface rounded-lg border border-app-line shadow-none p-6">
      <h3 className="text-sm font-bold text-app-muted uppercase tracking-wider mb-4 flex items-center gap-2">
        <Crosshair className="w-4 h-4 text-forest-600" />
        Why Now Map
      </h3>
      <div className="space-y-3">
        {mappings.map(({ useCase, matched }) => (
          <div key={useCase.rank} className="flex items-start gap-4 p-3 bg-app-canvas rounded-lg">
            <div className="flex-shrink-0 w-7 h-7 bg-forest-100 rounded-lg flex items-center justify-center">
              <span className="text-xs font-bold text-forest-700">#{useCase.rank}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-app-ink truncate">{useCase.title}</p>
              {matched.length > 0 ? (
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {matched.map((t) => (
                    <span
                      key={t.id}
                      className="inline-flex items-center gap-1 text-[10px] font-medium text-forest-700 bg-forest-50 px-2 py-0.5 rounded-full"
                    >
                      <UrgencyDot score={t.urgency_score} />
                      {t.title.length > 60 ? t.title.slice(0, 60) + '...' : t.title}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-[10px] text-app-muted mt-1">No matching triggers</p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
